import React, { useState, useEffect } from 'react';
import { Typography, Button, Grid } from '@mui/material';
import { PublicClientApplication } from '@azure/msal-browser';
import participantsApi from '../../../services/api/participants/participantsApi';
import { authConfig } from '../../../config/auth';
import paths from '../../../routes/paths';
import { iconMicrosoft } from '../../assets/images';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

const msalConfig = {
  auth: {
    clientId: authConfig.microsoft.clientId,
    authority: authConfig.microsoft.authority,
    redirectUri: authConfig.microsoft.redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
  system: { loggerOptions: { piiLoggingEnabled: false } }
};

const loginRequest = {
  scopes: ["User.Read", "profile", "openid", "email"],
  prompt: "select_account"
};

const MicrosoftLoginForm = ({ onBackClick }) => {
  const [loading, setLoading] = useState(false);
  const [msalInstance, setMsalInstance] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false); // Track MSAL initialization
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    async function initMsal() {
      try {
        const msalApp = new PublicClientApplication(msalConfig);
        await msalApp.initialize();
        if (isMounted) {
          setMsalInstance(msalApp);
          setIsInitialized(true);
        }
      } catch (e) {
        setError('Microsoft authentication unavailable');
        setIsInitialized(false);
      }
    }
    initMsal();
    return () => { isMounted = false; };
  }, []);

  const handleMicrosoftLogin = async () => {
    setError(null);
    setLoading(true);
    if (!msalInstance || !isInitialized) {
      setError('Microsoft login is not ready yet');
      setLoading(false);
      return;
    }
    try {
      const response = await msalInstance.loginPopup(loginRequest);
      if (response && response.account) {
        const account = response.account;
        const userInfo = {
          id: account.homeAccountId,
          name: account.name || account.username,
          email: account.username,
          provider: 'microsoft',
          accessToken: response.accessToken,
          idToken: response.idToken
        };
        const payload = { email: userInfo.email, idToken: response.idToken, provider: 'microsoft' };
        let authResponse = await participantsApi.verifyStudent(payload);
        if (typeof authResponse === 'string') {
          try { authResponse = JSON.parse(authResponse); } catch {}
        }
        if (authResponse && authResponse.studentId > 0) {
          localStorage.setItem('pupil', JSON.stringify({ ...authResponse, provider: 'microsoft', name: userInfo.name, email: userInfo.email }));
          window.location.href = paths.pupilSurveys;
        } else {
          setError('Microsoft account not linked to any student profile');
        }
      }
    } catch (error) {
      console.error('Microsoft login error:', error);
      let msg = 'Microsoft authentication failed';
      if (error && error.message) msg = error.message;
      if (error && error.response && error.response.data && error.response.data.message) msg = error.response.data.message;
      setError(msg);
    }
    setLoading(false);
  };

  return (
    <Grid container justifyContent="center">
      <Grid item lg={6} md={6} sm={11} xs={11}>
        <div className="formHeader">
          <div className="iconContainer">
            <img src={iconMicrosoft} alt="" />
          </div>
          <h2 className="formTitle">
            Login with Microsoft
          </h2>        
        </div>
        <div className="formDescription">
          Sign in with your school or organization account
        </div>
        {error && (
          <Typography color="error" sx={{ mb: 2 }} textAlign="center">{error}</Typography>
        )}

        <Button
          variant="contained"
          onClick={handleMicrosoftLogin}
          disabled={loading}
          className="msButton"   
          disableElevation
          disableRipple     
        >
          {loading ? 'Connecting...' : 'Sign in with Microsoft'}
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={onBackClick}
          className="backButton"
          disabled={loading}
          startIcon={<KeyboardBackspaceIcon />} disableRipple
        >
          Back to Login Options
        </Button>
      </Grid>
    </Grid>
  );
};

export default MicrosoftLoginForm;
